import { useState, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { DateTime } from 'luxon';
import { API_BASE_URL } from '../config';
import { enrichMissingIngredients, getRecipeMissingIngredients } from '../utils/recipeAvailability';

const createIdempotencyKey = (prefix = 'recipe_used') => {
    try {
        if (window?.crypto?.randomUUID) {
            return `${prefix}_${window.crypto.randomUUID()}`;
        }
    } catch (_) {
        // no-op
    }
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const clampServingSize = (value, fallback = 4) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return Math.max(1, Number(fallback || 4) || 4);
    return Math.max(1, Math.min(12, Math.round(parsed)));
};

const escapeHtml = (value = '') =>
    String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

const mealSlotQuestionMap = {
    breakfast: 'Şefim, bu kahvaltıyı kaç kişi tüketti?',
    lunch: 'Şefim, öğlen yemeğinde ailenin tamamı sofrada mıydı?',
    dinner: 'Şefim, akşam yemeğinde ailenin tamamı sofrada mıydı?'
};

async function confirmConsumedServingSize({ mealSlot, recommendedServingSize }) {
    const normalizedMealSlot = String(mealSlot || '').trim().toLowerCase();
    const defaultServingSize = clampServingSize(recommendedServingSize, 4);
    const question = mealSlotQuestionMap[normalizedMealSlot] || 'Şefim, bu öğünü kaç kişi tüketti?';

    const result = await Swal.fire({
        title: 'Son Kontrol',
        html: `
            <div style="text-align:left">
                <p style="margin:0 0 12px 0;font-weight:700;">${question}</p>
                <p style="margin:0 0 14px 0;font-size:12px;color:#64748b;">Varsayılan öneri: ${defaultServingSize} kişi</p>
                <div style="display:flex;align-items:center;justify-content:center;gap:12px;">
                    <button id="consume-serving-decrease" type="button" class="swal2-styled" style="background:#e2e8f0;color:#0f172a;padding:10px 14px;">-</button>
                    <div style="min-width:84px;text-align:center;">
                        <div id="consume-serving-value" style="font-size:28px;font-weight:900;line-height:1;">${defaultServingSize}</div>
                        <div style="font-size:11px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;color:#94a3b8;">Kişi</div>
                    </div>
                    <button id="consume-serving-increase" type="button" class="swal2-styled" style="background:#e2e8f0;color:#0f172a;padding:10px 14px;">+</button>
                </div>
            </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Kaydet ve stoğu düş',
        cancelButtonText: 'Vazgeç',
        confirmButtonColor: '#f1592a',
        didOpen: () => {
            let current = defaultServingSize;
            const valueEl = document.getElementById('consume-serving-value');
            const sync = () => {
                if (valueEl) valueEl.textContent = String(current);
            };
            document.getElementById('consume-serving-decrease')?.addEventListener('click', () => {
                current = clampServingSize(current - 1, 1);
                sync();
            });
            document.getElementById('consume-serving-increase')?.addEventListener('click', () => {
                current = clampServingSize(current + 1, current);
                sync();
            });
            Swal.getPopup().__consumedServingSize = () => current;
        },
        preConfirm: () => {
            const getter = Swal.getPopup()?.__consumedServingSize;
            return clampServingSize(typeof getter === 'function' ? getter() : defaultServingSize, defaultServingSize);
        }
    });

    if (!result.isConfirmed) return null;
    return clampServingSize(result.value, defaultServingSize);
}

/**
 * useMenuApproval Hook
 * Manages the approval flow for a menu suggestion, including ingredient selection for shopping list.
 * 
 * @param {Object} options
 * @param {Function} options.onSuccess - Callback after successful approval
 * @param {Function} options.onError - Callback on error
 * @param {Boolean} options.showSuccessModal - Whether to show the default success modal (default: true)
 */
const useMenuApproval = ({ onSuccess, onError, showSuccessModal = true } = {}) => {
    const [isApproving, setIsApproving] = useState(false);

    const approveMenu = useCallback(async (recipes, options = {}) => {
        if (!recipes || recipes.length === 0) return;
        const {
            skipInteractiveShopping = false,
            suppressBulkShoppingList = false,
            sourceSurface = 'unknown_surface',
            mealSlot = null,
            currentServingSize = null
        } = options;

        setIsApproving(true);
        try {
            // 1. Identify missing ingredients from all recipes (fallback ID'leri filtrele)
            const allMissing = recipes.flatMap(r =>
                getRecipeMissingIngredients(r)
                    .filter(ing => {
                        const dictId = String(ing?.dictionaryId || '');
                        return !dictId || !dictId.startsWith('fallback_');
                    })
                    .map(ing => ({ ...ing, recipeName: r.name }))
            );

            let ingredientsToAdd = [];

            // 2. If there are missing ingredients, ask user which ones to add to shopping list
            if (allMissing.length > 0 && !skipInteractiveShopping) {
                // Unique missing list by name
                const uniqueMissing = [];
                const seen = new Set();
                allMissing.forEach(ing => {
                    const key = String(ing.name || ing.ingredientName || '').toLowerCase();
                    if (key && !seen.has(key)) {
                        seen.add(key);
                        uniqueMissing.push(ing);
                    }
                });
                const enrichedMissing = enrichMissingIngredients(uniqueMissing);
                const criticalCount = enrichedMissing.filter((ing) => ing.missingSeverity?.severity === 'critical').length;
                const skippableCount = enrichedMissing.filter((ing) => ing.missingSeverity?.canContinueWithout).length;

                const swalResult = await Swal.fire({
                    title: criticalCount > 0 ? 'Ana Malzeme Eksik' : 'Eksik Malzemeler',
                    text: `${recipes.length === 1 ? 'Bu tarif' : 'Bu plan'} için mutfağınızda görünmeyen ${enrichedMissing.length} ürün var.`,
                    html: `
                        <div style="text-align:left;margin-bottom:12px;padding:12px;border-radius:12px;background:${criticalCount > 0 ? '#fff7ed' : '#f8fafc'};border:1px solid ${criticalCount > 0 ? '#fed7aa' : '#e2e8f0'};">
                            <div style="font-size:13px;font-weight:800;color:${criticalCount > 0 ? '#c2410c' : '#334155'};">
                                ${criticalCount > 0
                                    ? `${criticalCount} ana malzeme eksik. İstersen yine de yapabilirsin ama tarif sonucu değişebilir.`
                                    : `${skippableCount} eksik malzeme yemek yapmaya engel görünmüyor.`}
                            </div>
                            <div style="margin-top:4px;font-size:11px;color:#64748b;">
                                Toz biber, baharat ve küçük tamamlayıcılar için "malzeme olmadan devam et" seçeneği kullanılabilir.
                            </div>
                        </div>
                        <div class="swal-ingredient-list" style="text-align: left; max-height: 400px; overflow-y: auto; padding: 15px; background: var(--color-background-base); border-radius: 12px; border: 1px solid var(--color-border-base, #e2e8f0); margin-top: 15px;">
                            ${enrichedMissing.map((ing, idx) => {
                                const severity = ing.missingSeverity || {};
                                const isCritical = severity.severity === 'critical';
                                const badgeBg = isCritical ? '#fee2e2' : severity.severity === 'skippable' ? '#dcfce7' : '#fef3c7';
                                const badgeColor = isCritical ? '#b91c1c' : severity.severity === 'skippable' ? '#15803d' : '#b45309';
                                return `
                                <label for="ing-${idx}" class="flex items-center p-3 mb-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700">
                                    <input type="checkbox" id="ing-${idx}" value="${ing.name}" checked style="margin-right: 12px; width: 20px; height: 20px; accent-color: #ef4444;">
                                    <div class="flex flex-col">
                                        <span class="text-sm font-bold" style="color: var(--color-text-base)">${escapeHtml(ing.name)}</span>
                                        <span style="display:inline-flex;width:max-content;margin-top:4px;padding:2px 7px;border-radius:999px;background:${badgeBg};color:${badgeColor};font-size:10px;font-weight:800;">${escapeHtml(severity.label || 'Eksik malzeme')}</span>
                                        ${ing.recipeName ? `<span class="text-[10px] text-slate-400">Tarif: ${escapeHtml(ing.recipeName)}</span>` : ''}
                                    </div>
                                </label>
                            `; }).join('')}
                        </div>
                        <p class="text-[11px] text-slate-500 mt-3 italic text-center">İstediğiniz ürünleri seçip listenize tek tıkla ekleyebilirsiniz.</p>
                    `,
                    icon: criticalCount > 0 ? 'warning' : 'question',
                    showDenyButton: true,
                    showCancelButton: true,
                    showCloseButton: true,
                    confirmButtonText: 'Sepete Ekle ve Devam Et',
                    denyButtonText: criticalCount > 0 ? 'Yine de Yap' : 'Malzeme Olmadan Devam Et',
                    cancelButtonText: 'Vazgeç',
                    width: '450px',
                    confirmButtonColor: '#ef4444',
                    denyButtonColor: criticalCount > 0 ? '#f97316' : '#10b981',
                    cancelButtonColor: '#94a3b8',
                    allowOutsideClick: true,
                    background: 'var(--color-background-base)',
                    color: 'var(--color-text-base)',
                    preConfirm: () => {
                        return enrichedMissing
                            .filter((_, idx) => document.getElementById(`ing-${idx}`).checked)
                            .map(ing => ({
                                name: ing.name,
                                amount:
                                    ing.needed?.amount ??
                                    ing.canonicalNeed?.amount ??
                                    ing.amount ??
                                    ing.suggestedAmount ??
                                    0,
                                unit:
                                    ing.needed?.unit ||
                                    ing.canonicalNeed?.unit ||
                                    ing.unit ||
                                    'adet',
                                dictionaryId: ing.dictionaryId
                            }));
                    }
                });

                // 🟢 CRITICAL: Handle Dismissal (Backdrop/Escape/Close Button)
                if (swalResult.isDismissed) {
                    setIsApproving(false);
                    return; // Stop the flow entirely
                }

                if (swalResult.isConfirmed) {
                    ingredientsToAdd = swalResult.value || [];
                }
                // Deny means "continue without adding missing ingredients".
            } else {
                // No missing ingredients
                if (!skipInteractiveShopping) {
                    const result = await Swal.fire({
                        title: 'Menüyü Onaylıyor Musunuz?',
                        text: 'Tüm malzemeleriniz elinizde görünüyor. Afiyet olsun!',
                        icon: 'success',
                        showCancelButton: true,
                        confirmButtonText: 'Evet, Onayla',
                        cancelButtonText: 'İptal',
                        confirmButtonColor: '#f1592a'
                    });
                    if (!result.isConfirmed) {
                        setIsApproving(false);
                        return;
                    }
                }
            }

            const token = localStorage.getItem('authToken') || localStorage.getItem('token');

            // 🧠 Intelligence: Record Menu Approval (100% Signal)
            try {
                await axios.post(`${API_BASE_URL}/api/intelligence/event`, {
                    type: 'MENU_APPROVED',
                    payload: {
                        recipes: recipes.map(r => ({ id: r.id, name: r.name || r.title })),
                        count: recipes.length
                    }
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (e) {
                console.warn('[Intelligence] Menu Approval Event failed', e);
            }

            const recommendedServingSize = clampServingSize(
                currentServingSize
                || recipes?.[0]?.presentation?.appliedPortion
                || recipes?.[0]?.targetPortion
                || recipes?.[0]?.servings
                || 4,
                4
            );
            let consumedServingSize = recommendedServingSize;
            if (mealSlot) {
                const confirmedServingSize = await confirmConsumedServingSize({
                    mealSlot,
                    recommendedServingSize
                });
                if (!confirmedServingSize) {
                    setIsApproving(false);
                    return;
                }
                consumedServingSize = confirmedServingSize;
            }

            // 3. Mark recipes as used with reason 'menu_approval'
            // We do this in parallel for speed
            await Promise.all(recipes.map((recipe) => {
                const idempotencyKey = createIdempotencyKey(`menu_approval_${recipe?.id || 'unknown'}`);
                return axios.post(`${API_BASE_URL}/api/recipes/used`, {
                    recipeData: recipe,
                    usedPortion: consumedServingSize,
                    recommendationServingSize: recommendedServingSize,
                    usedReason: 'menu_approval',
                    sourceSurface,
                    mealSlot,
                    idempotencyKey
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'X-Idempotency-Key': idempotencyKey
                    }
                });
            }));

            // 4. BATCH ADD missing ingredients to shopping list if any
            if (ingredientsToAdd.length > 0 && !suppressBulkShoppingList) {
                console.log(`🚀 Sending batch shopping list request: ${ingredientsToAdd.length} items`);
                await axios.post(`${API_BASE_URL}/api/shopping-list/add-items-bulk`, {
                    itemNames: ingredientsToAdd,
                    listName: recipes.length > 1 ? `Eksik Malzeme Planı (${DateTime.now().toLocaleString(DateTime.DATE_SHORT)})` : null
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (showSuccessModal) {
                    await Swal.fire({
                        title: 'Harika!',
                        text: 'Menü onaylandı ve eksik malzemeler alışveriş listesine eklendi.',
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            } else {
                if (showSuccessModal) {
                    await Swal.fire({
                        title: 'Afiyet Olsun!',
                        text: 'Menü başarıyla onaylandı.',
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            }


            // 🔄 TRIGGER SIDEBAR SYNC
            window.dispatchEvent(new Event('Alakart_SYNC'));

            if (onSuccess) onSuccess(options);
        } catch (error) {
            console.error('Menu Approval Error:', error);
            const msg = error.response?.data?.message || error.message || 'İşlem sırasında bir hata oluştu.';
            Swal.fire('Hata', msg, 'error');
            if (onError) onError(error);
        } finally {
            setIsApproving(false);
        }
    }, [onSuccess, onError]);

    return { approveMenu, isApproving };
};

export default useMenuApproval;
